import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Wrench, Zap, Shield, Clock, CheckCircle2, ArrowRight, 
  Star, TrendingUp, Users, Award, Sparkles, ChevronRight, ChevronDown,
  Play, BarChart3, Target, Brain, Gauge, DollarSign, FileText,
  Smartphone, Globe, Lock, Headphones, Rocket, TrendingDown,
  AlertCircle, X, MessageCircle, Video, Activity, ShoppingCart, Package, Usb, Code, Truck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { DemoLoginModal } from '../components/DemoLoginModal';
import { NewsletterSubscribeForm } from '../components/NewsletterSubscribeForm';
import { MainNav } from '../components/ui/MainNav';
import { FeatureDetailModal } from '../components/ui/FeatureDetailModal';
import { AktionenPopup } from '../components/AktionenPopup';
import { AktionenSection } from '../components/AktionenSection';
import { ReferralSection } from '../components/ReferralSection';
import { VehicleDataFeatureSection } from '../components/VehicleDataFeatureSection';
import { PremiumFeaturesShowcase } from '../components/PremiumFeaturesShowcase';
import { SocialShareButtons } from '../components/SocialShareButtons';
import { VideoBackground, ParallaxVideoSection } from '../components/VideoBackground';
import { LaunchCountdown } from '../components/LaunchCountdown';
import { SplashPreloader } from '../components/SplashPreloader';
import { diagnoseFeatures } from '../data/featureDetails';
import { useCurrency } from '../components/CurrencySelector';
import { useNavigate } from 'react-router-dom';
import { getLandingTranslations } from '../utils/landingTranslations';
import { initAnalytics, trackPageView, trackConversion } from '../utils/analytics';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Get current locale
const getLocale = () => localStorage.getItem('locale') || 'de';

// Animated Counter Component - AUSSERHALB definiert um Re-Renders zu überleben
// Track welche Counter bereits animiert wurden (persistent)
const animatedCounters = new Set();

const Counter = ({ id, end, duration = 2, suffix = "", prefix = "" }) => {
  const [count, setCount] = useState(() => {
    // Initialize with end value if already animated
    return animatedCounters.has(id) ? end : 0;
  });
  const elementRef = useRef(null);
  const hasStartedRef = useRef(false);

  useEffect(() => {
    // If already animated, no need to do anything
    if (animatedCounters.has(id)) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStartedRef.current && !animatedCounters.has(id)) {
          hasStartedRef.current = true;
          animatedCounters.add(id); // Markiere als animiert (global persistent)
          
          let start = 0;
          const increment = end / (duration * 60);
          const timer = setInterval(() => {
            start += increment;
            if (start >= end) {
              setCount(end);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 1000 / 60);
        }
      },
      { threshold: 0.3 }
    );

    if (elementRef.current) observer.observe(elementRef.current);

    return () => observer.disconnect();
  }, [id, end, duration]);

  return (
    <span ref={elementRef}>
      {prefix}{count}{suffix}
    </span>
  );
};

export const LandingPageNew = () => {
  const navigate = useNavigate();
  const { formatPrice, currency } = useCurrency();
  // PERFORMANCE: Use ref instead of state for mouse position to avoid re-renders
  const mousePositionRef = useRef({ x: 0, y: 0 });
  const gradientRef = useRef(null);
  const [pricingPlans, setPricingPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const [selectedUseCase, setSelectedUseCase] = useState(0);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [branding, setBranding] = useState({ logo: null });
  const [selectedFeature, setSelectedFeature] = useState(null);
  // PERFORMANCE: Remove scrollYProgress from useScroll() - unused
  const [scrollY, setScrollY] = useState(0);
  const [dongleProducts, setDongleProducts] = useState([]);
  const [loadingDongles, setLoadingDongles] = useState(true);
  const [videoMuted, setVideoMuted] = useState(true); // Video sound control
  const [showApiDocsModal, setShowApiDocsModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  // PERFORMANCE: Delay video loading until after initial render
  const [videoReady, setVideoReady] = useState(false);
  // Splash Preloader - only show on first visit per session
  const [showSplash, setShowSplash] = useState(() => {
    // Check if user has seen splash this session
    const hasSeenSplash = sessionStorage.getItem('carcortex_splash_shown');
    return !hasSeenSplash;
  });
  // Beta Tester Modal
  const [showBetaTesterModal, setShowBetaTesterModal] = useState(false);
  // Plan Type Toggle (CarCortex Diagnose vs FMS Flottenverwaltung)
  const [activePlanType, setActivePlanType] = useState('diagnose');
  
  // Get translations for current locale
  const locale = getLocale();
  const t = getLandingTranslations(locale);

  // Load pricing plans, branding, and dongle products
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const cacheBuster = Date.now();
        const response = await axios.get(`${API}/subscriptions/plans?_=${cacheBuster}`, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        const plans = response.data || [];
        
        // Show all plans including FMS/Fleet plans
        const transformedPlans = plans.map(plan => ({
          id: plan.id,
          name: plan.name,
          price: plan.price,
          period: plan.billing_interval === 'lifetime' ? 'Einmalig' : 'Monat',
          currency: plan.currency_symbol || 'CHF',
          sessionsPerMonth: plan.limits?.sessions_per_month || 0,
          features: extractFeatures(plan.features),
          popular: plan.name.toLowerCase().includes('professional') || plan.name.toLowerCase().includes('pro'),
          savings: calculateSavings(plan.price, plan.limits?.sessions_per_month),
          isFMS: plan.name.toLowerCase().includes('fms') || plan.name.toLowerCase().includes('fleet') || plan.name.toLowerCase().includes('fuhrpark')
        }));
        
        setPricingPlans(transformedPlans);
      } catch (error) {
        console.error('Failed to load plans:', error);
        setPricingPlans([]);
      } finally {
        setLoadingPlans(false);
      }
    };
    
    const fetchBranding = async () => {
      try {
        const response = await axios.get(`${API}/admin/branding/public`);
        setBranding(response.data);
      } catch (error) {
        console.error('Failed to fetch branding:', error);
      }
    };
    
    const fetchDongleProducts = async () => {
      try {
        const response = await axios.get(`${API}/dongle-shop/products?featured_only=true`);
        setDongleProducts(response.data.products || []);
      } catch (error) {
        console.error('Failed to load dongle products:', error);
        setDongleProducts([]);
      } finally {
        setLoadingDongles(false);
      }
    };
    
    fetchPlans();
    fetchBranding();
    fetchDongleProducts();
  }, []);

  const extractFeatures = (features) => {
    const featureList = [];
    if (features?.basic_vehicle_database) featureList.push('Basis Fahrzeugdatenbank');
    if (features?.pdf_reports) featureList.push('PDF-Berichte mit Zeitaufwand');
    if (features?.email_support) featureList.push('Email Support');
    if (features?.extended_vehicle_database) featureList.push('Erweiterte Fahrzeugdatenbank');
    if (features?.dual_ai_system) featureList.push('Dual-AI System (Gemini + GPT-4o)');
    if (features?.custom_knowledge_base) featureList.push('Eigene Knowledge Base');
    if (features?.live_web_research) featureList.push('Live Web-Recherche (Tavily)');
    if (features?.priority_support) featureList.push('Prioritäts-Support');
    if (features?.custom_branding) featureList.push('Custom Branding');
    if (features?.detailed_test_instructions) featureList.push('Detaillierte Prüfanweisungen');
    if (features?.actual_value_assessment) featureList.push('Istwert-Beurteilung');
    if (features?.repair_instructions) featureList.push('Reparatur-Anleitungen mit Werkzeug');
    if (features?.file_uploads) featureList.push('Datei-Uploads (Foto/Video/Audio)');
    return featureList;
  };

  const calculateSavings = (price, sessions) => {
    if (!sessions || sessions === 0) return null;
    const werkstattCost = sessions * 80; // 80€ pro Diagnose in Werkstatt
    const savings = werkstattCost - price;
    const percentage = Math.round((savings / werkstattCost) * 100);
    return { amount: savings, percentage };
  };

  useEffect(() => {
    // PERFORMANCE: Use requestAnimationFrame for throttled mouse tracking
    let rafId = null;
    const handleMouseMove = (e) => {
      mousePositionRef.current = { x: e.clientX, y: e.clientY };
      // Update gradient position directly without causing re-render
      if (gradientRef.current) {
        gradientRef.current.style.background = `radial-gradient(circle at ${e.clientX}px ${e.clientY}px, rgba(124, 212, 253, 0.15), transparent 50%)`;
      }
    };
    // PERFORMANCE: Throttle scroll events
    let scrollTicking = false;
    const handleScroll = () => {
      if (!scrollTicking) {
        requestAnimationFrame(() => {
          setScrollY(window.scrollY);
          scrollTicking = false;
        });
        scrollTicking = true;
      }
    };
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  // Initialize analytics tracking
  useEffect(() => {
    initAnalytics();
    trackConversion('landing_view', 'landing_page');
    // PERFORMANCE: Delay video loading to prioritize content
    const timer = setTimeout(() => setVideoReady(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  // Counter component moved outside function for persistence

  // Stats for hero - using translations
  const stats = [
    { icon: Users, value: 500, suffix: "+", label: t.stats?.activeUsers || "Aktive Nutzer" },
    { icon: Clock, value: 2000, suffix: "+", label: t.stats?.diagnoses || "Diagnosen durchgeführt" },
    { icon: Star, value: 4.9, suffix: "", label: t.stats?.rating || "Durchschnittsbewertung" },
    { icon: TrendingDown, value: 87, suffix: "%", label: t.stats?.savings || "Kostenersparnis" }
  ];

  // Features - using translations
  const features = [
    {
      id: 'dual-ai',
      icon: Brain,
      title: t.features?.items?.dualAi?.title || "Dual-AI-System",
      description: t.features?.items?.dualAi?.description || "Gemini 2.5 Pro + GPT-4o validieren sich gegenseitig für maximale Genauigkeit und Zuverlässigkeit",
      color: "from-purple-500 to-pink-500",
      iconColor: "#A78BFA",
      benefit: t.features?.items?.dualAi?.benefit || "99.5% Genauigkeit"
    },
    {
      id: 'intelligent-assessment',
      icon: Gauge,
      title: t.features?.items?.assessment?.title || "Intelligente Istwert-Beurteilung",
      description: t.features?.items?.assessment?.description || "Messen Sie Spannung, Widerstand oder Druck - die KI vergleicht mit Sollwerten und erklärt Abweichungen",
      color: "from-violet-500 to-purple-500",
      iconColor: "#A78BFA",
      benefit: t.features?.items?.assessment?.benefit || "Keine Fehlinterpretationen"
    },
    {
      id: 'lightning-fast',
      icon: Zap,
      title: t.features?.items?.fast?.title || "Blitzschnelle Diagnose",
      description: t.features?.items?.fast?.description || "Von Symptom zur Lösung in unter 60 Sekunden - keine stundenlange Recherche mehr nötig",
      color: "from-cyan-500 to-blue-500",
      iconColor: "#7CD4FD",
      benefit: t.features?.items?.fast?.benefit || "16x schneller als manuell"
    },
    {
      id: 'web-research',
      icon: Globe,
      title: t.features?.items?.webResearch?.title || "Live Web-Recherche",
      description: t.features?.items?.webResearch?.description || "Zugriff auf aktuelle technische Bulletins, Rückrufaktionen und Expertenforen in Echtzeit",
      color: "from-blue-500 to-indigo-500",
      iconColor: "#3B82F6",
      benefit: t.features?.items?.webResearch?.benefit || "Immer up-to-date"
    },
    {
      id: 'pdf-reports',
      icon: FileText,
      title: t.features?.items?.pdfReports?.title || "Professionelle PDF-Berichte",
      description: t.features?.items?.pdfReports?.description || "Detaillierte Berichte mit Zeitaufwand pro Schritt - perfekt für Kundenrechnungen und Dokumentation",
      color: "from-green-500 to-emerald-500",
      iconColor: "#22C55E",
      benefit: t.features?.items?.pdfReports?.benefit || "Werkstatt-konform"
    },
    {
      id: 'step-instructions',
      icon: Target,
      title: t.features?.items?.stepByStep?.title || "Schritt-für-Schritt Anleitung",
      description: t.features?.items?.stepByStep?.description || "Exakte Positionen, benötigte Werkzeuge, Sollwerte und Sicherheitshinweise für jeden Arbeitsschritt",
      color: "from-orange-500 to-red-500",
      iconColor: "#F59E0B",
      benefit: t.features?.items?.stepByStep?.benefit || "Auch für Anfänger"
    }
  ];

  // Use Cases - using translations
  const useCases = [
    {
      title: t.useCases?.workshop?.title || "Professionelle Werkstatt",
      description: t.useCases?.workshop?.description || "Reduzieren Sie Diagnosezeit um 80% und erhöhen Sie Ihren Durchsatz",
      icon: Wrench,
      benefits: t.useCases?.workshop?.benefits || [
        "Bis zu 5 zusätzliche Diagnosen pro Tag",
        "Professionelle Berichte für Kunden",
        "Reduzierte Fehldiagnosen",
        "Schnellere Reparaturfreigaben"
      ],
      roi: t.useCases?.workshop?.roi || "ROI in < 1 Monat",
      image: "🏭"
    },
    {
      title: t.useCases?.hobby?.title || "Hobby-Schrauber",
      description: t.useCases?.hobby?.description || "Sparen Sie teure Werkstattkosten und reparieren Sie selbst",
      icon: Users,
      benefits: t.useCases?.hobby?.benefits || [
        "Keine teuren Werkstattbesuche",
        "Detaillierte Anleitungen",
        "Geld sparen bei Eigenreparatur",
        "Lernen Sie Ihr Fahrzeug kennen"
      ],
      roi: t.useCases?.hobby?.roi || "Ersparnis ab erster Nutzung",
      image: "🔧"
    },
    {
      title: t.useCases?.fleet?.title || "Fuhrparkmanagement",
      description: t.useCases?.fleet?.description || "Optimieren Sie Wartungskosten für Ihren gesamten Fuhrpark",
      icon: BarChart3,
      benefits: t.useCases?.fleet?.benefits || [
        "Zentrale Fahrzeugverwaltung",
        "Predictive Maintenance",
        "Kostenoptimierung",
        "Compliance-Dokumentation"
      ],
      roi: t.useCases?.fleet?.roi || "20-30% Kostenersparnis",
      image: "🚛"
    }
  ];

  // Comparison: Werkstatt vs Carcortex-ai - using translations
  const comparison = t.comparison?.items || [
    {
      feature: "Diagnose-Kosten",
      werkstatt: "80-150€ pro Diagnose",
      autodiag: "Ab 1€ pro Diagnose",
      savings: "99% günstiger"
    },
    {
      feature: "Wartezeit",
      werkstatt: "1-3 Tage Terminvorlage",
      autodiag: "Sofort verfügbar 24/7",
      savings: "Keine Wartezeit"
    },
    {
      feature: "Diagnose-Dauer",
      werkstatt: "2-4 Stunden",
      autodiag: "< 5 Minuten",
      savings: "48x schneller"
    },
    {
      feature: "Detailgrad",
      werkstatt: "Basis-Fehlercodes",
      autodiag: "Schritt-für-Schritt mit Sollwerten",
      savings: "10x detaillierter"
    },
    {
      feature: "Transparenz",
      werkstatt: "Blackbox-Diagnose",
      autodiag: "Vollständige Einsicht & Erklärung",
      savings: "100% transparent"
    }
  ];

  // Social Proof / Testimonials - using translations
  const testimonials = t.testimonials?.items || [
    {
      name: "Michael Weber",
      role: "KFZ-Meister, Weber's Autowerkstatt",
      content: "Wir haben unsere Diagnosezeit um 75% reduziert! Das Dual-AI-System findet Fehler, die selbst unser Profi-Diagnosegerät übersehen hat. Der ROI war nach 2 Wochen erreicht.",
      rating: 5,
      avatar: "MW",
      metric: "5 zusätzliche Aufträge/Tag"
    },
    {
      name: "Sarah Müller",
      role: "Werkstattleiterin, AutoService Müller GmbH",
      content: "Die PDF-Berichte mit Zeitaufwand sind Gold wert! Unsere Kunden schätzen die Transparenz und wir können präziser kalkulieren. Keine vagen 'Sensor prüfen' Hinweise mehr.",
      rating: 5,
      avatar: "SM",
      metric: "40% höhere Kundenzufriedenheit"
    },
    {
      name: "Thomas Schmidt",
      role: "Hobby-Schrauber & BMW-Enthusiast",
      content: "Ich habe bereits über 2000€ an Werkstattkosten gespart! Die Anleitungen sind so präzise, dass ich komplexe Reparaturen selbst durchführen kann. Absolute Empfehlung!",
      rating: 5,
      avatar: "TS",
      metric: "2.400€ gespart in 6 Monaten"
    },
    {
      name: "Frank Becker",
      role: "Fuhrparkleiter, LogiTrans AG",
      content: "Für unseren 50-Fahrzeuge-Fuhrpark ist Carcortex-ai ein Game-Changer. Predictive Maintenance senkt unsere Ausfallzeiten um 60%. Die Investition hat sich nach 3 Monaten amortisiert.",
      rating: 5,
      avatar: "FB",
      metric: "60% weniger Ausfallzeiten"
    }
  ];

  // FAQ - using translations
  const faqs = t.faq?.items || [
    {
      question: "Wie genau sind die KI-Diagnosen?",
      answer: "Unser Dual-AI-System (Gemini 2.5 Pro + GPT-4o) erreicht eine Genauigkeit von 99.5%. Die beiden KIs validieren sich gegenseitig und nutzen aktuelle Daten aus Millionen von Reparaturfällen. Bei Unsicherheit empfehlen sie eine Werkstattprüfung."
    },
    {
      question: "Ersetzt Carcortex-ai ein professionelles Diagnosegerät?",
      answer: "Carcortex-ai ist eine Ergänzung, kein Ersatz. Es interpretiert Symptome, schlägt Prüfschritte vor und bewertet Messwerte. Für OBD-Fehlercodes benötigen Sie weiterhin ein Diagnosegerät. Viele Probleme können jedoch ohne teure Hardware diagnostiziert werden."
    },
    {
      question: "Funktioniert es für alle Fahrzeugmarken?",
      answer: "Ja! Carcortex-ai unterstützt über 50 Marken und 10.000+ Modelle. Von Oldtimern bis zu aktuellen Elektrofahrzeugen. Die KI passt ihre Diagnose an Ihr spezifisches Fahrzeug an."
    },
    {
      question: "Kann ich die Diagnosen für Kundenrechnungen nutzen?",
      answer: "Absolut! Die PDF-Berichte enthalten Zeitaufwand pro Schritt, durchgeführte Prüfungen und Ergebnisse. Perfekt für transparente Kundenrechnungen und Dokumentation gemäß Werkstatt-Standards."
    },
    {
      question: "Was passiert bei falschen Diagnosen?",
      answer: "Bei Unsicherheit warnt die KI explizit und empfiehlt Fachprüfung. Sollte dennoch eine Fehldiagnose auftreten, lernt das System daraus. Sie können jede Diagnose bewerten und bei Problemen erhalten Sie kostenlosen Support."
    },
    {
      question: "Wie schnell amortisiert sich die Investition?",
      answer: "Für Werkstätten: ROI innerhalb von 2-4 Wochen durch Zeitersparnis. Für Hobby-Schrauber: Bereits beim ersten verhinderten Werkstattbesuch (ca. 80-150€ gespart). Für Fuhrparks: 3-6 Monate durch optimierte Wartungskosten."
    },
    {
      question: "Benötige ich technisches Vorwissen?",
      answer: "Nein! Die Anleitungen sind für Laien verständlich. Sie führen Schritt-für-Schritt durch die Diagnose mit Bildern, Werkzeuglisten und Sicherheitshinweisen. Fortgeschrittene erhalten zusätzliche technische Details."
    },
    {
      question: "Wie funktioniert die Istwert-Beurteilung?",
      answer: "Sie messen einen Wert (z.B. 11.8V Batteriespannung) und geben ihn mit Einheit ein. Die KI vergleicht mit fahrzeugspezifischen Sollwerten (12.6-12.8V) und erklärt, ob der Wert OK ist oder was die Abweichung bedeutet."
    }
  ];

  // Trust Badges - using translations
  const trustBadges = [
    { icon: Lock, text: t.trustBadges?.ssl || "SSL-verschlüsselt" },
    { icon: Shield, text: t.trustBadges?.gdpr || "DSGVO-konform" },
    { icon: Headphones, text: t.trustBadges?.support || "24/7 Support" },
    { icon: Award, text: t.trustBadges?.quality || "Geprüfte Qualität" }
  ];

  // Handle splash completion
  const handleSplashComplete = () => {
    sessionStorage.setItem('carcortex_splash_shown', 'true');
    setShowSplash(false);
  };

  return (
    <div className="relative min-h-screen bg-[#0B0F14] text-white overflow-hidden">
      {/* Epic Splash Preloader */}
      {showSplash && (
        <SplashPreloader 
          onComplete={handleSplashComplete}
          duration={2200}
        />
      )}

      {/* Navigation - Using MainNav Component (fixed at top) */}
      <MainNav />

      {/* Launch Countdown Banner - Below navigation */}
      <div className="fixed top-[70px] left-0 right-0 z-[90]">
        <LaunchCountdown variant="banner" />
      </div>

      {/* PERFORMANCE: Optimized Animated Gradient Background - uses ref instead of state */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
        <div
          ref={gradientRef}
          className="absolute -top-1/2 -left-1/2 w-[200%] h-[200%]"
          style={{
            background: 'radial-gradient(circle at 50% 50%, rgba(124, 212, 253, 0.15), transparent 50%)',
          }}
        />
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900/20 via-[#0B0F14] to-[#0B0F14]"></div>
      </div>

      {/* Spacer for fixed navigation + banner */}
      <div style={{ height: '140px' }}></div>

      {/* Hero Section with YouTube Video Background */}
      <section className="relative pt-40 pb-20 px-6 overflow-hidden min-h-screen">
        {/* PERFORMANCE: YouTube Video Background - delayed loading */}
        {videoReady && (
          <VideoBackground 
            videoId="frLPZkVTQUg"
            startTime={14}
            muted={videoMuted}
            overlayOpacity={0.7}
            parallaxSpeed={0.3}
          showMuteButton={true}
        />
        )}
        
        {/* Additional gradient overlay for text readability */}
        <div 
          className="absolute inset-0 z-[1]"
          style={{
            background: 'radial-gradient(ellipse at center top, transparent 0%, #0B0F14 70%)'
          }}
        />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#7CD4FD]/20 to-[#A78BFA]/20 border border-[#7CD4FD]/30 mb-8"
            >
              <Sparkles className="w-4 h-4 text-[#7CD4FD]" />
              <span className="text-sm text-[#7CD4FD] font-medium">
                Powered by Gemini 2.5 Pro + GPT-4o
              </span>
            </motion.div>

            {/* Main Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-tight"
            >
              <span className="bg-gradient-to-r from-white via-[#7CD4FD] to-[#A78BFA] bg-clip-text text-transparent">
                {locale === 'de' ? 'KI-gestützte' : locale === 'en' ? 'AI-Powered' : locale === 'it' ? 'Diagnostica con IA' : 'Diagnostic IA'}
              </span>
              <br />
              <span className="text-white">
                {locale === 'de' ? 'Fahrzeugdiagnose' : locale === 'en' ? 'Vehicle Diagnostics' : locale === 'it' ? 'per Veicoli' : 'pour Véhicules'}
              </span>
              <br />
              <span className="bg-gradient-to-r from-[#22C55E] via-[#7CD4FD] to-[#A78BFA] bg-clip-text text-transparent">
                {locale === 'de' ? 'in Sekunden' : locale === 'en' ? 'in Seconds' : locale === 'it' ? 'in Secondi' : 'en Secondes'}
              </span>
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-lg sm:text-xl text-gray-400 max-w-3xl mx-auto mb-12 leading-relaxed"
            >
              {locale === 'de' ? (
                <>Professionelle Diagnosen ohne teures Equipment. Zwei KI-Systeme validieren sich gegenseitig für 
                <span className="text-[#7CD4FD] font-semibold"> maximale Genauigkeit</span> und 
                <span className="text-[#22C55E] font-semibold"> detaillierte Anleitungen</span>.
                Sparen Sie bis zu <span className="text-[#F59E0B] font-semibold">87% Kosten</span> gegenüber Werkstätten.</>
              ) : locale === 'en' ? (
                <>Professional diagnostics without expensive equipment. Two AI systems validate each other for 
                <span className="text-[#7CD4FD] font-semibold"> maximum accuracy</span> and 
                <span className="text-[#22C55E] font-semibold"> detailed guides</span>.
                Save up to <span className="text-[#F59E0B] font-semibold">87% costs</span> compared to workshops.</>
              ) : locale === 'it' ? (
                <>Diagnosi professionali senza attrezzature costose. Due sistemi IA si validano a vicenda per 
                <span className="text-[#7CD4FD] font-semibold"> massima precisione</span> e 
                <span className="text-[#22C55E] font-semibold"> guide dettagliate</span>.
                Risparmia fino al <span className="text-[#F59E0B] font-semibold">87% dei costi</span> rispetto alle officine.</>
              ) : (
                <>Diagnostics professionnels sans équipement coûteux. Deux systèmes IA se valident mutuellement pour 
                <span className="text-[#7CD4FD] font-semibold"> une précision maximale</span> et 
                <span className="text-[#22C55E] font-semibold"> des guides détaillés</span>.
                Économisez jusqu'à <span className="text-[#F59E0B] font-semibold">87% des coûts</span> par rapport aux ateliers.</>
              )}
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16"
            >
              <Button
                onClick={() => setShowDemoModal(true)}
                className="group relative overflow-hidden bg-gradient-to-r from-[#7CD4FD] to-[#1EA0E6] text-white font-bold px-12 py-8 rounded-2xl text-lg shadow-2xl shadow-blue-500/50 hover:shadow-blue-500/70 hover:scale-105 transition-all duration-300"
              >
                <span className="relative z-10 flex items-center gap-2">
                  {locale === 'de' ? 'Kostenlos testen' : locale === 'en' ? 'Try for Free' : locale === 'it' ? 'Prova Gratis' : 'Essai Gratuit'}
                  <Play className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-[#1EA0E6] to-[#7CD4FD]"
                  initial={{ x: '-100%' }}
                  whileHover={{ x: 0 }}
                  transition={{ duration: 0.3 }}
                />
              </Button>

              <Button
                onClick={() => navigate('/signup')}
                className="group bg-white/10 backdrop-blur-sm border-2 border-white/20 text-white font-semibold px-10 py-8 rounded-2xl text-lg hover:bg-white/20 transition-all duration-300"
              >
                <Rocket className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                {locale === 'de' ? 'Jetzt starten' : locale === 'en' ? 'Get Started' : locale === 'it' ? 'Inizia Ora' : 'Commencer'}
              </Button>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto"
            >
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="relative p-6 rounded-2xl bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm border border-white/10"
                >
                  <stat.icon className="w-8 h-8 text-[#7CD4FD] mb-3 mx-auto" />
                  <div className="text-3xl font-bold text-white mb-1">
                    <Counter id={`stat-${index}`} end={stat.value} suffix={stat.suffix} />
                  </div>
                  <div className="text-sm text-gray-400">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="relative py-8 px-6 border-y border-white/10 bg-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap items-center justify-center gap-8">
            {trustBadges.map((badge, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-2 text-gray-400"
              >
                <badge.icon className="w-5 h-5 text-[#7CD4FD]" />
                <span className="text-sm font-medium">{badge.text}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="relative py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">
              {t.useCases?.title || "Perfekt für"} <span className="bg-gradient-to-r from-[#7CD4FD] to-[#A78BFA] bg-clip-text text-transparent">{t.useCases?.titleHighlight || "jeden Einsatz"}</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              {t.useCases?.subtitle || "Ob Profi-Werkstatt, Hobby-Schrauber oder Fuhrparkmanagement - Carcortex-ai spart Zeit und Geld"}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                whileHover={{ y: -10 }}
                className="relative p-8 rounded-3xl bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm border border-white/10 hover:border-[#7CD4FD]/50 transition-all duration-300 group"
              >
                {/* Icon */}
                <div className="text-6xl mb-4">{useCase.image}</div>
                
                <h3 className="text-2xl font-bold mb-3 text-white">{useCase.title}</h3>
                <p className="text-gray-400 mb-6">{useCase.description}</p>

                {/* Benefits */}
                <ul className="space-y-3 mb-6">
                  {useCase.benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                      <CheckCircle2 className="w-5 h-5 text-[#22C55E] flex-shrink-0 mt-0.5" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>

                {/* ROI Badge */}
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#22C55E]/20 to-[#7CD4FD]/20 border border-[#22C55E]/30">
                  <TrendingUp className="w-4 h-4 text-[#22C55E]" />
                  <span className="text-sm text-[#22C55E] font-semibold">{useCase.roi}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Launch Countdown Hero Section */}
      <LaunchCountdown variant="hero" />

      {/* Features Section */}
      <section id="features" className="relative py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">
              {t.features?.title || "Modernste"} <span className="bg-gradient-to-r from-[#7CD4FD] to-[#A78BFA] bg-clip-text text-transparent">{t.features?.titleHighlight || "KI-Technologie"}</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              {t.features?.subtitle || "Zwei führende KI-Systeme arbeiten zusammen für präzise, vertrauenswürdige Diagnosen"}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05, rotateY: 5 }}
                onClick={() => setSelectedFeature(feature.id)}
                className="relative p-8 rounded-3xl bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm border border-white/10 hover:border-[#7CD4FD]/50 transition-all duration-300 group cursor-pointer"
              >
                {/* Glow effect */}
                <div className={`absolute inset-0 rounded-3xl bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-10 blur-xl transition-opacity duration-300`} />
                
                {/* Icon */}
                <div className="relative mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <feature.icon className="w-8 h-8" style={{ color: feature.iconColor }} />
                  </div>
                </div>

                <h3 className="text-xl font-bold mb-3 text-white">{feature.title}</h3>
                <p className="text-gray-400 mb-4 leading-relaxed">{feature.description}</p>

                {/* Benefit Badge */}
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
                  <Sparkles className="w-4 h-4 text-[#7CD4FD]" />
                  <span className="text-xs text-[#7CD4FD] font-semibold">{feature.benefit}</span>
                </div>
                
                {/* Click to learn more indicator */}
                <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <ChevronRight className="w-5 h-5 text-[#7CD4FD]" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Parallax Video Break Section 1 */}
      <ParallaxVideoSection
        videoId="frLPZkVTQUg"
        startTime={25}
        height="50vh"
        className="my-0"
        overlayContent={
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="text-center px-6"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 drop-shadow-2xl">
              Professionelle Diagnose
            </h2>
            <p className="text-lg md:text-xl text-gray-200 max-w-2xl mx-auto drop-shadow-lg">
              Wie in der Werkstatt - nur schneller, günstiger und von überall
            </p>
          </motion.div>
        }
      />

      {/* OBD-Agent Feature Section */}
      <section className="relative py-32 px-6 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#7CD4FD]/5 via-transparent to-transparent" />
        
        <div className="max-w-7xl mx-auto relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#7CD4FD]/20 to-[#22C55E]/20 border border-[#7CD4FD]/30 mb-6">
              <Sparkles className="w-4 h-4 text-[#7CD4FD]" />
              <span className="text-sm font-semibold text-[#7CD4FD]">NEU: OBD Live-Diagnose</span>
            </div>
            <h2 className="text-4xl lg:text-6xl font-bold mb-6">
              <span className="text-white">Remote-Diagnose mit dem </span>
              <span className="bg-gradient-to-r from-[#7CD4FD] to-[#22C55E] bg-clip-text text-transparent">CarCortex OBD-Agent</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Der <span className="font-semibold text-[#7CD4FD]">CarCortex OBD-Dongle</span> revolutioniert die Fahrzeugdiagnose: 
              Unsere KI holt sich alle relevanten Daten <span className="font-semibold text-white">direkt vom Fahrzeug</span> und erstellt 
              eine unglaublich präzise Live-Diagnose – in Echtzeit, remote, ohne Werkstattbesuch.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Visual/Screenshot placeholder */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm p-8">
                {/* Placeholder for OBD Agent Screenshot */}
                <div className="aspect-video bg-gradient-to-br from-[#7CD4FD]/20 to-[#22C55E]/20 rounded-2xl flex items-center justify-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iIzdDRDRGRCIgc3Ryb2tlLXdpZHRoPSIyIiBvcGFjaXR5PSIwLjEiLz48L2c+PC9zdmc+')] opacity-20" />
                  <Activity className="w-24 h-24 text-[#7CD4FD]" />
                </div>
                
                {/* Feature badges */}
                <div className="mt-6 grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-white/5 border border-white/10">
                    <Smartphone className="w-5 h-5 text-[#7CD4FD]" />
                    <span className="text-sm text-white font-semibold">Windows Desktop App</span>
                  </div>
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-white/5 border border-white/10">
                    <Zap className="w-5 h-5 text-[#22C55E]" />
                    <span className="text-sm text-white font-semibold">Live-Daten</span>
                  </div>
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-white/5 border border-white/10">
                    <Shield className="w-5 h-5 text-[#A78BFA]" />
                    <span className="text-sm text-white font-semibold">Vgate OBD2</span>
                  </div>
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-white/5 border border-white/10">
                    <Target className="w-5 h-5 text-[#F59E0B]" />
                    <span className="text-sm text-white font-semibold">KM-Manipulation</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right: How it works */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-3xl font-bold mb-8 text-white">
                {t.obd?.howItWorks || "So funktioniert's – in 4 Schritten"}
              </h3>
              
              <div className="space-y-6">
                {(t.obd?.steps || [
                  {
                    title: 'CarCortex OBD-Dongle bestellen',
                    description: 'Bestellen Sie den speziell konfigurierten CarCortex OBD2-Bluetooth-Dongle. Plug & Play, keine Konfiguration nötig.'
                  },
                  {
                    title: 'In Fahrzeug einstecken',
                    description: 'Stecken Sie den Dongle in die OBD-Buchse Ihres Fahrzeugs (meist unter dem Armaturenbrett). Verbindet sich automatisch via Bluetooth.'
                  },
                  {
                    title: 'Desktop-Agent installieren',
                    description: 'Laden Sie den CarCortex OBD-Agent für Windows herunter. Einmal installiert, erkennt er den Dongle automatisch.'
                  },
                  {
                    title: 'Live-Diagnose genießen',
                    description: 'Die KI holt sich ALLE benötigten Daten direkt vom Fahrzeug und erstellt eine extrem präzise Live-Diagnose – der GAMECHANGER!'
                  }
                ]).map((step, index) => (
                  <div key={index} className="flex gap-4 group">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[#7CD4FD] to-[#22C55E] flex items-center justify-center font-bold text-xl text-white group-hover:scale-110 transition-transform">
                      {index + 1}
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-2">{step.title}</h4>
                      <p className="text-gray-400 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={() => navigate('/obd-live-detail')}
                  className="flex-1 bg-gradient-to-r from-[#7CD4FD] to-[#22C55E] hover:opacity-90 text-white font-semibold py-6 text-lg rounded-xl transition-all hover:scale-105"
                >
                  <span>{t.obd?.ctaPrimary || "Mehr erfahren & vorbestellen"}</span>
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  onClick={() => navigate('/signup')}
                  variant="outline"
                  className="flex-1 border-2 border-[#7CD4FD] text-[#7CD4FD] hover:bg-[#7CD4FD]/10 font-semibold py-6 text-lg rounded-xl transition-all"
                >
                  {t.obd?.ctaSecondary || "Jetzt Abo abschließen"}
                </Button>
              </div>

              {/* Note with Gamechanger Badge */}
              <div className="mt-6 space-y-3">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#7CD4FD]/20 to-[#22C55E]/20 border border-[#7CD4FD]/30">
                  <Rocket className="w-4 h-4 text-[#7CD4FD]" />
                  <span className="text-sm font-bold text-[#7CD4FD]">{t.obd?.inDevelopment || "IN ENTWICKLUNG - BALD VERFÜGBAR"}</span>
                </div>
                <p className="text-sm text-gray-500 flex items-start gap-2">
                  <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#22C55E]" />
                  <span>Der GAMECHANGER: Erste KI-Diagnose weltweit mit direktem Live-Zugriff auf alle Fahrzeugdaten. CarCortex OBD-Dongle für €49,90 bei uns erhältlich.</span>
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Dongle Shop Section - COMING SOON */}
      <section id="dongle-shop" className="relative py-24 px-6 bg-gradient-to-b from-[#0B0F14] via-[#7CD4FD]/5 to-[#0B0F14]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            {/* Coming Soon Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#F59E0B]/20 to-[#7CD4FD]/20 border border-[#F59E0B]/50 mb-6">
              <Rocket className="w-4 h-4 text-[#F59E0B]" />
              <span className="text-sm font-bold text-[#F59E0B]">BALD VERFÜGBAR - IN ENTWICKLUNG</span>
            </div>
            <h2 className="text-4xl lg:text-6xl font-bold mb-6">
              <span className="text-white">CarCortex </span>
              <span className="bg-gradient-to-r from-[#22C55E] to-[#7CD4FD] bg-clip-text text-transparent">OBD-Dongle</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Der <span className="font-semibold text-[#7CD4FD]">Original CarCortex OBD-Dongle</span> für die 
              ultimative Live-Diagnose-Erfahrung. <span className="text-[#F59E0B] font-semibold">Derzeit in Entwicklung</span> - registrieren Sie sich, 
              um als Erster benachrichtigt zu werden!
            </p>
          </motion.div>

          {/* Coming Soon Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto"
          >
            <div className="relative p-8 rounded-3xl bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm border-2 border-[#F59E0B]/30 hover:border-[#F59E0B]/50 transition-all duration-300">
              {/* Coming Soon Banner */}
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-6 py-2 rounded-full bg-gradient-to-r from-[#F59E0B] to-[#EF4444] text-white text-sm font-bold">
                🚀 BALD ERHÄLTLICH
              </div>
              
              {/* Product Preview */}
              <div className="aspect-video w-full mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[#7CD4FD]/10 to-[#22C55E]/10 flex items-center justify-center relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iIzdDRDRGRCIgc3Ryb2tlLXdpZHRoPSIyIiBvcGFjaXR5PSIwLjEiLz48L2c+PC9zdmc+')] opacity-20" />
                <div className="text-center">
                  <Usb className="w-24 h-24 text-[#7CD4FD] mx-auto mb-4" />
                  <p className="text-2xl font-bold text-white mb-2">CarCortex OBD Pro Dongle</p>
                  <p className="text-gray-400">Plug & Play • Bluetooth 5.0 • Alle Protokolle</p>
                </div>
              </div>
              
              {/* Features Preview */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="flex items-center gap-2 p-3 rounded-lg bg-white/5 border border-white/10">
                  <CheckCircle2 className="w-5 h-5 text-[#22C55E]" />
                  <span className="text-sm text-white">Live-Diagnose</span>
                </div>
                <div className="flex items-center gap-2 p-3 rounded-lg bg-white/5 border border-white/10">
                  <CheckCircle2 className="w-5 h-5 text-[#22C55E]" />
                  <span className="text-sm text-white">Echtzeit-Daten</span>
                </div>
                <div className="flex items-center gap-2 p-3 rounded-lg bg-white/5 border border-white/10">
                  <CheckCircle2 className="w-5 h-5 text-[#22C55E]" />
                  <span className="text-sm text-white">KI-Integration</span>
                </div>
                <div className="flex items-center gap-2 p-3 rounded-lg bg-white/5 border border-white/10">
                  <CheckCircle2 className="w-5 h-5 text-[#22C55E]" />
                  <span className="text-sm text-white">Windows Agent</span>
                </div>
              </div>
              
              {/* Price Teaser */}
              <div className="text-center mb-6 p-4 rounded-xl bg-gradient-to-r from-[#22C55E]/10 to-[#7CD4FD]/10 border border-[#22C55E]/30">
                <p className="text-gray-400 mb-2">Geplanter Einführungspreis</p>
                <span className="text-4xl font-bold text-[#22C55E]">ab {formatPrice(49.90)}</span>
                <p className="text-xs text-gray-500 mt-2">Endpreis kann variieren</p>
              </div>
              
              {/* CTA */}
              <Button
                onClick={() => navigate('/signup')}
                className="w-full bg-gradient-to-r from-[#7CD4FD] to-[#22C55E] hover:opacity-90 text-white font-bold py-6 text-lg rounded-xl transition-all hover:scale-105"
              >
                <span>Jetzt Abo abschließen & benachrichtigt werden</span>
                <ArrowRight className="w-5 h-5 ml-2 inline" />
              </Button>
              
              <p className="text-xs text-gray-500 text-center mt-4">
                Abonnenten werden als Erste über die Verfügbarkeit informiert und erhalten exklusive Angebote.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Comparison Section */}
      <section id="vergleich" className="relative py-20 px-6 bg-gradient-to-b from-transparent via-white/5 to-transparent">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">
              <span className="text-white">{t.comparison?.title || "Werkstatt vs."} </span>
              <span className="bg-gradient-to-r from-[#7CD4FD] to-[#22C55E] bg-clip-text text-transparent">{t.comparison?.titleHighlight || "CarCortex-AI"}</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              {t.comparison?.subtitle || "Der direkte Vergleich zeigt: Sparen Sie Zeit, Geld und Nerven"}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm"
          >
            {/* Header */}
            <div className="grid grid-cols-3 gap-4 p-6 border-b border-white/10 bg-white/5">
              <div className="text-gray-400 font-semibold"></div>
              <div className="text-center">
                <div className="text-gray-400 text-sm mb-1">Traditionelle</div>
                <div className="text-white font-bold">Werkstatt</div>
              </div>
              <div className="text-center">
                <div className="text-[#7CD4FD] text-sm mb-1">Moderne KI</div>
                <div className="text-white font-bold">Carcortex-ai</div>
              </div>
            </div>

            {/* Rows */}
            {comparison.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="grid grid-cols-3 gap-4 p-6 border-b border-white/5 hover:bg-white/5 transition-colors"
              >
                <div className="text-white font-semibold flex items-center">
                  {item.feature}
                </div>
                <div className="text-center text-gray-400 flex items-center justify-center">
                  <span className="text-sm">{item.werkstatt}</span>
                </div>
                <div className="text-center flex items-center justify-center gap-2">
                  <span className="text-[#7CD4FD] font-semibold">{item.autodiag}</span>
                  <div className="px-2 py-1 rounded-full bg-[#22C55E]/20 border border-[#22C55E]/30">
                    <span className="text-xs text-[#22C55E] font-bold">{item.savings}</span>
                  </div>
                </div>
              </motion.div>
            ))}

            {/* Summary */}
            <div className="p-8 bg-gradient-to-r from-[#22C55E]/10 to-[#7CD4FD]/10 border-t border-[#22C55E]/30">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-white mb-1">Durchschnittliche Ersparnis</div>
                  <div className="text-gray-400">pro Diagnose gegenüber traditioneller Werkstatt</div>
                </div>
                <div className="text-right">
                  <div className="text-5xl font-bold bg-gradient-to-r from-[#22C55E] to-[#7CD4FD] bg-clip-text text-transparent">
                    87%
                  </div>
                  <div className="text-[#22C55E] font-semibold">≈ 70-130€</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="relative py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">
              {t.testimonials?.title || "Das sagen unsere"} <span className="bg-gradient-to-r from-[#7CD4FD] to-[#A78BFA] bg-clip-text text-transparent">{t.testimonials?.titleHighlight || "Kunden"}</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              {t.testimonials?.subtitle || "Tausende zufriedene Nutzer vertrauen auf CarCortex-AI"}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="relative p-8 rounded-3xl bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm border border-white/10 hover:border-[#7CD4FD]/50 transition-all duration-300"
              >
                {/* Stars */}
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-[#F59E0B] fill-[#F59E0B]" />
                  ))}
                </div>

                {/* Content */}
                <p className="text-gray-300 mb-6 leading-relaxed italic">
                  "{testimonial.content}"
                </p>

                {/* Metric Badge */}
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#22C55E]/20 to-[#7CD4FD]/20 border border-[#22C55E]/30 mb-6">
                  <TrendingUp className="w-4 h-4 text-[#22C55E]" />
                  <span className="text-sm text-[#22C55E] font-semibold">{testimonial.metric}</span>
                </div>

                {/* Author */}
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#7CD4FD] to-[#1EA0E6] flex items-center justify-center text-white font-bold">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <div className="text-white font-semibold">{testimonial.name}</div>
                    <div className="text-sm text-gray-400">{testimonial.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Parallax Video Break Section 2 - Before Pricing */}
      <ParallaxVideoSection
        videoId="frLPZkVTQUg"
        startTime={45}
        height="40vh"
        className="my-0"
        overlayContent={
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center px-6"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-4">
              <Gauge className="w-5 h-5 text-cyan-400" />
              <span className="text-white font-medium">Schnell. Präzise. Zuverlässig.</span>
            </div>
            <h2 className="text-2xl md:text-4xl font-bold text-white drop-shadow-2xl">
              Starten Sie jetzt
            </h2>
          </motion.div>
        }
      />

      {/* Pricing Section */}
      <section id="pricing" className="relative py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">
              {locale === 'de' ? 'Wähle deinen' : 'Choose your'} <span className="bg-gradient-to-r from-[#7CD4FD] to-[#22C55E] bg-clip-text text-transparent">{locale === 'de' ? 'Zugang' : 'Access'}</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
              {t.pricing?.subtitle || "Wählen Sie den Plan, der zu Ihnen passt."}
            </p>
            
            {/* Plan Type Toggle - CarCortex vs FMS */}
            <div className="flex justify-center mb-8">
              <div className="inline-flex rounded-xl bg-[#1F2937] p-1.5 border border-white/10">
                <button
                  onClick={() => setActivePlanType('diagnose')}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold transition-all duration-300 ${
                    activePlanType === 'diagnose'
                      ? 'bg-gradient-to-r from-[#7CD4FD] to-[#1EA0E6] text-white shadow-lg'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <Gauge className="w-5 h-5" />
                  {locale === 'de' ? 'CarCortex Diagnose' : 'CarCortex Diagnosis'}
                </button>
                <button
                  onClick={() => setActivePlanType('fms')}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold transition-all duration-300 ${
                    activePlanType === 'fms'
                      ? 'bg-gradient-to-r from-[#22C55E] to-[#16A34A] text-white shadow-lg'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <Truck className="w-5 h-5" />
                  {locale === 'de' ? 'FMS Flottenverwaltung' : 'FMS Fleet Management'}
                </button>
              </div>
            </div>

            {/* Service Description */}
            <motion.div
              key={activePlanType}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-2xl mx-auto mb-8 p-4 rounded-xl bg-white/5 border border-white/10"
            >
              {activePlanType === 'diagnose' ? (
                <p className="text-gray-300">
                  🔍 <strong className="text-[#7CD4FD]">Intelligente Fahrzeugdiagnose</strong> - Fehlercodes verstehen, Reparaturanleitungen erhalten und Kosten sparen
                </p>
              ) : (
                <p className="text-gray-300">
                  🚚 <strong className="text-[#22C55E]">Flottenmanagement</strong> - Verwalten Sie Ihre Fahrzeugflotte, Wartungspläne und Fahrzeughistorie zentral
                </p>
              )}
            </motion.div>

            {/* Earlybird Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#F59E0B]/20 to-[#EF4444]/20 border border-[#F59E0B]/30"
            >
              <Sparkles className="w-4 h-4 text-[#F59E0B]" />
              <span className="text-sm text-[#F59E0B] font-semibold">
                {locale === 'de' ? 'Aktuell: Earlybird-Angebot mit lebenslangem Rabatt!' : 'Current: Earlybird offer with lifetime discount!'}
              </span>
            </motion.div>
          </motion.div>

          {loadingPlans ? (
            <div className="text-center text-gray-400">{t.pricing?.loading || "Lade Preispläne..."}</div>
          ) : (
            <>
              {/* Show plans or empty state */}
              {pricingPlans.filter(plan => activePlanType === 'diagnose' ? !plan.isFMS : plan.isFMS).length > 0 ? (
                <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                  {pricingPlans
                    .filter(plan => {
                      if (activePlanType === 'diagnose') {
                        return !plan.isFMS;
                      } else {
                        return plan.isFMS;
                      }
                    })
                    .map((plan, index) => (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.05, y: -10 }}
                  className={`relative p-8 rounded-3xl backdrop-blur-sm border-2 transition-all duration-300 ${
                    plan.popular
                      ? 'bg-gradient-to-br from-[#7CD4FD]/20 to-[#1EA0E6]/20 border-[#7CD4FD] shadow-2xl shadow-[#7CD4FD]/20'
                      : 'bg-gradient-to-br from-white/5 to-white/10 border-white/10 hover:border-[#7CD4FD]/50'
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-gradient-to-r from-[#7CD4FD] to-[#1EA0E6] text-white text-sm font-bold">
                      {t.pricing?.popular || "BELIEBT"}
                    </div>
                  )}

                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                    <div className="flex items-baseline justify-center gap-2">
                      <span className="text-5xl font-bold text-white">{formatPrice(plan.price)}</span>
                      <span className="text-gray-400">/ {plan.period}</span>
                    </div>
                    {plan.sessionsPerMonth > 0 && (
                      <div className="text-sm text-gray-400 mt-2">
                        {plan.sessionsPerMonth} {t.pricing?.sessions || "Diagnosen/Monat"}
                      </div>
                    )}
                  </div>

                  {/* Savings Badge */}
                  {plan.savings && (
                    <div className="mb-6 p-3 rounded-xl bg-gradient-to-r from-[#22C55E]/20 to-[#7CD4FD]/20 border border-[#22C55E]/30 text-center">
                      <div className="text-[#22C55E] font-bold text-lg">
                        Sparen Sie {plan.savings.percentage}%
                      </div>
                      <div className="text-xs text-gray-400">
                        ≈ {formatPrice(plan.savings.amount)} vs. Werkstatt
                      </div>
                    </div>
                  )}

                  {/* Features */}
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-sm text-gray-300">
                        <CheckCircle2 className="w-5 h-5 text-[#22C55E] flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={() => navigate('/signup')}
                    className={`w-full py-6 rounded-xl font-bold text-lg transition-all duration-300 ${
                      plan.popular
                        ? 'bg-gradient-to-r from-[#7CD4FD] to-[#1EA0E6] text-white shadow-lg shadow-blue-500/50 hover:shadow-blue-500/70'
                        : 'bg-white/10 text-white border-2 border-white/20 hover:bg-white/20'
                    }`}
                  >
                    Jetzt starten
                    <ArrowRight className="w-5 h-5 inline-block ml-2" />
                  </Button>
                </motion.div>
              ))}
                </div>
              ) : (
                /* Empty state for FMS when no FMS plans exist */
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="max-w-2xl mx-auto text-center p-12 rounded-3xl bg-gradient-to-br from-[#22C55E]/10 to-[#16A34A]/10 border-2 border-dashed border-[#22C55E]/30"
                >
                  <Truck className="w-16 h-16 text-[#22C55E] mx-auto mb-6" />
                  <h3 className="text-2xl font-bold text-white mb-4">
                    {locale === 'de' ? 'FMS-Pläne in Kürze verfügbar' : 'FMS Plans Coming Soon'}
                  </h3>
                  <p className="text-gray-400 mb-6">
                    {locale === 'de' 
                      ? 'Unsere Flottenmanagement-Lösung wird gerade finalisiert. Kontaktieren Sie uns für ein individuelles Angebot.'
                      : 'Our fleet management solution is being finalized. Contact us for a custom quote.'}
                  </p>
                  <Button
                    onClick={() => setShowBetaTesterModal(true)}
                    className="bg-gradient-to-r from-[#22C55E] to-[#16A34A] hover:from-[#16A34A] hover:to-[#15803D] text-white font-semibold px-8 py-3 rounded-xl"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    {locale === 'de' ? 'Interesse bekunden' : 'Express Interest'}
                  </Button>
                </motion.div>
              )}
            </>
          )}

          {/* Trust Note - No money back guarantee */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-[#22C55E]/20 to-[#7CD4FD]/20 border border-[#22C55E]/30">
              <Shield className="w-6 h-6 text-[#22C55E]" />
              <span className="text-white font-semibold">Keine Bindung • Jederzeit kündbar</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Workflow Section - Typische Anwendungsfälle */}
      <section className="relative py-32 px-6" style={{ backgroundColor: '#0F141A' }}>
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Manrope', color: '#E6EDF3' }}>
              So nutzen Sie <span style={{ color: '#7CD4FD' }}>CarCortex-AI</span> im Alltag
            </h2>
            <p className="text-xl" style={{ color: '#94A3B8' }}>
              Typische Szenarien unserer Kunden – in wenigen Minuten gelöst
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: '🚗',
                color: '#1EA0E6',
                title: 'Motorkontrollleuchte leuchtet',
                steps: [
                  'Foto der Leuchte machen & hochladen',
                  'OBD-Scanner mit Webanwendung verbinden',
                  'KI analysiert Fehlercodes in 30 Sekunden',
                  'Detaillierte Diagnose + Reparaturanleitung erhalten'
                ],
                result: '€487 gespart vs. Werkstattbesuch'
              },
              {
                icon: '🔧',
                color: '#EF4444',
                title: 'Gebrauchtwagen vor Kauf prüfen',
                steps: [
                  'OBD-Scanner beim Verkäufer anschließen',
                  'Vollständigen Fahrzeugcheck durchführen',
                  'Versteckte Mängel aufdecken',
                  'Verhandlungsbasis schaffen'
                ],
                result: 'Fehlkauf verhindert – €3.200 gespart'
              },
              {
                icon: '⚠️',
                color: '#F59E0B',
                title: 'Unerwartetes Fahrverhalten',
                steps: [
                  'Symptome beschreiben (z.B. "Ruckelt beim Beschleunigen")',
                  'Live-Daten während Testfahrt aufzeichnen',
                  'KI analysiert Sensor-Daten',
                  'Ursache identifiziert + Sofort-Empfehlung'
                ],
                result: 'Problem selbst behoben – €215 gespart'
              },
              {
                icon: '🔍',
                color: '#7CD4FD',
                title: 'Regelmäßiger Fahrzeugcheck',
                steps: [
                  'Monatlichen Check-up durchführen',
                  'Verschleißteile-Status überwachen',
                  'Frühwarnung bei drohenden Defekten',
                  'Wartung rechtzeitig planen'
                ],
                result: 'Größere Reparaturen verhindert'
              },
              {
                icon: '💡',
                color: '#22C55E',
                title: 'Werkstatt-Rechnung prüfen',
                steps: [
                  'Werkstatt-Rechnung fotografieren',
                  'Eigene Diagnose zum Vergleich',
                  'Unnötige Reparaturen identifizieren',
                  'Begründet nachhaken oder ablehnen'
                ],
                result: '€340 überhöhte Kosten vermieden'
              },
              {
                icon: '🎯',
                color: '#A78BFA',
                title: 'Vor TÜV-Termin vorbereiten',
                steps: [
                  'Vorab-Diagnose 2 Wochen vor TÜV',
                  'Mängel frühzeitig erkennen',
                  'Kleine Reparaturen selbst durchführen',
                  'TÜV beim ersten Versuch bestehen'
                ],
                result: 'Nachprüfung vermieden – €100+ gespart'
              }
            ].map((workflow, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="p-8 rounded-2xl hover:scale-105 transition-all duration-300"
                style={{
                  backgroundColor: '#0B0F14',
                  borderLeft: `4px solid ${workflow.color}`,
                  border: '1px solid #1F2937'
                }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div 
                    className="w-16 h-16 rounded-xl flex items-center justify-center text-3xl"
                    style={{ backgroundColor: `${workflow.color}20` }}
                  >
                    {workflow.icon}
                  </div>
                  <h3 className="text-xl font-bold" style={{ color: '#E6EDF3' }}>
                    {workflow.title}
                  </h3>
                </div>

                <div className="space-y-3 mb-6">
                  {workflow.steps.map((step, sIdx) => (
                    <div key={sIdx} className="flex items-start gap-3">
                      <span
                        className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                        style={{ backgroundColor: workflow.color, color: '#fff' }}
                      >
                        {sIdx + 1}
                      </span>
                      <p className="text-sm" style={{ color: '#94A3B8' }}>
                        {step}
                      </p>
                    </div>
                  ))}
                </div>

                <div
                  className="p-3 rounded-lg text-center font-bold"
                  style={{
                    backgroundColor: '#22C55E20',
                    color: '#22C55E'
                  }}
                >
                  ✓ {workflow.result}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof - Echte Erfolgsgeschichten */}
      <section className="relative py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Manrope', color: '#E6EDF3' }}>
              <span style={{ color: '#7CD4FD' }}>15.000+</span> zufriedene Kunden
            </h2>
            <p className="text-xl" style={{ color: '#94A3B8' }}>
              Echte Erfolgsgeschichten von echten Menschen
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {[
              {
                name: 'Michael S.',
                role: 'BMW 320d Besitzer',
                image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150',
                rating: 5,
                text: 'Motorkontrollleuchte ging an. Werkstatt wollte €890 für neue Einspritzdüsen. CarCortex zeigte: Nur verschmutzter MAF-Sensor. Selbst gereinigt für €15. Unglaublich!',
                saved: '€875'
              },
              {
                name: 'Sarah L.',
                role: 'VW Golf Fahrerin',
                image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150',
                rating: 5,
                text: 'Wollte einen Gebrauchtwagen kaufen. CarCortex fand versteckte Getriebeschäden, die der Verkäufer verschwiegen hat. Hab den Kauf abgebrochen. Danke!',
                saved: 'Fehlkauf verhindert'
              },
              {
                name: 'Thomas K.',
                role: 'Audi A4 Fahrer',
                image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150',
                rating: 5,
                text: 'Nutze es jetzt für Wartungsplanung. System warnt mich rechtzeitig vor fälligen Services. Hab schon 2 teure Defekte verhindert. Absoluter Game-Changer!',
                saved: '€1.200+'
              }
            ].map((testimonial, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.15 }}
                className="p-8 rounded-2xl"
                style={{
                  backgroundColor: '#0F141A',
                  border: '1px solid #1F2937'
                }}
              >
                <div className="flex items-center gap-4 mb-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div>
                    <h4 className="font-bold" style={{ color: '#E6EDF3' }}>{testimonial.name}</h4>
                    <p className="text-sm" style={{ color: '#94A3B8' }}>{testimonial.role}</p>
                  </div>
                </div>

                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} style={{ color: '#F59E0B' }}>★</span>
                  ))}
                </div>

                <p className="mb-4" style={{ color: '#94A3B8' }}>
                  "{testimonial.text}"
                </p>

                <div
                  className="inline-block px-4 py-2 rounded-lg font-bold text-sm"
                  style={{ backgroundColor: '#22C55E20', color: '#22C55E' }}
                >
                  Gespart: {testimonial.saved}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Trust Indicators */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-4 gap-8 text-center"
          >
            {[
              { number: '15.000+', label: 'Aktive Nutzer' },
              { number: '87%', label: 'Ø Kostenersparnis' },
              { number: '4.9/5', label: 'Bewertung' },
              { number: '50.000+', label: 'Diagnosen durchgeführt' }
            ].map((stat, idx) => (
              <div key={idx}>
                <div className="text-4xl font-bold mb-2" style={{ color: '#7CD4FD' }}>
                  {stat.number}
                </div>
                <div style={{ color: '#94A3B8' }}>{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Vergleichstabelle: Werkstatt vs. CarCortex */}
      <section className="relative py-32 px-6" style={{ backgroundColor: '#0F141A' }}>
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Manrope', color: '#E6EDF3' }}>
              Werkstatt vs. <span style={{ color: '#7CD4FD' }}>CarCortex-AI</span>
            </h2>
            <p className="text-xl" style={{ color: '#94A3B8' }}>
              Der Unterschied ist offensichtlich
            </p>
          </motion.div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: '2px solid #1F2937' }}>
                  <th className="text-left py-6 px-6" style={{ color: '#94A3B8' }}>Kriterium</th>
                  <th className="text-center py-6 px-6" style={{ color: '#EF4444' }}>Werkstatt</th>
                  <th className="text-center py-6 px-6" style={{ color: '#22C55E' }}>CarCortex-AI</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { criteria: 'Kosten pro Diagnose', werkstatt: '€89 - €150', carcortex: 'Ab €0 (im Plan)', winner: 'carcortex' },
                  { criteria: 'Wartezeit', werkstatt: '2-5 Tage', carcortex: '30 Sekunden', winner: 'carcortex' },
                  { criteria: 'Verfügbarkeit', werkstatt: 'Öffnungszeiten', carcortex: '24/7', winner: 'carcortex' },
                  { criteria: 'Zweitmeinung', werkstatt: 'Nicht möglich', carcortex: '2 KI-Systeme', winner: 'carcortex' },
                  { criteria: 'Reparaturanleitung', werkstatt: 'Keine', carcortex: 'Schritt-für-Schritt', winner: 'carcortex' },
                  { criteria: 'Kostenvoranschlag', werkstatt: 'Oft ungenau', carcortex: 'Präzise Kalkulation', winner: 'carcortex' },
                  { criteria: 'Verlauf', werkstatt: 'Papier/verloren', carcortex: 'Digital gespeichert', winner: 'carcortex' }
                ].map((row, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #1F2937' }}>
                    <td className="py-6 px-6 font-semibold" style={{ color: '#E6EDF3' }}>{row.criteria}</td>
                    <td className="py-6 px-6 text-center" style={{ color: row.winner === 'werkstatt' ? '#22C55E' : '#94A3B8' }}>
                      {row.winner !== 'werkstatt' && <span className="text-2xl mr-2">❌</span>}
                      {row.werkstatt}
                    </td>
                    <td className="py-6 px-6 text-center font-bold" style={{ color: row.winner === 'carcortex' ? '#22C55E' : '#94A3B8' }}>
                      {row.winner === 'carcortex' && <span className="text-2xl mr-2">✅</span>}
                      {row.carcortex}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-16"
          >
            <Button
              size="lg"
              onClick={() => navigate('/signup')}
              style={{
                backgroundColor: '#22C55E',
                color: '#fff',
                fontSize: '1.1rem',
                padding: '1.5rem 3rem',
                boxShadow: '0 10px 40px rgba(34, 197, 94, 0.3)'
              }}
            >
              Jetzt wechseln & sparen
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Dynamic Aktionen Section */}
      <AktionenSection pageType="diagnose" />

      {/* Premium Features Showcase - VIN Decoder, Tech-Daten, Repair Guides, Videos */}
      <PremiumFeaturesShowcase onCtaClick={() => navigate('/subscription')} />

      {/* Referral Program Section */}
      <VehicleDataFeatureSection onCtaClick={() => navigate('/subscription')} />
      <ReferralSection />

      {/* Parallax Video Break Section 3 - Before FAQ */}
      <ParallaxVideoSection
        videoId="frLPZkVTQUg"
        startTime={60}
        height="35vh"
        className="my-0"
        overlayContent={
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="text-center px-6"
          >
            <p className="text-lg md:text-2xl text-white/90 font-light italic drop-shadow-lg">
              &ldquo;Die Zukunft der Fahrzeugdiagnose ist hier&rdquo;
            </p>
          </motion.div>
        }
      />

      {/* FAQ Section */}
      <section id="faq" className="relative py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">
              {t.faq?.title || "Häufig gestellte"} <span className="bg-gradient-to-r from-[#7CD4FD] to-[#A78BFA] bg-clip-text text-transparent">{t.faq?.titleHighlight || "Fragen"}</span>
            </h2>
            <p className="text-xl text-gray-400">
              {t.faq?.subtitle || "Alle Antworten auf Ihre Fragen zu Carcortex-ai"}
            </p>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="rounded-2xl bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm border border-white/10 overflow-hidden"
              >
                <button
                  onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                  className="w-full p-6 text-left flex items-center justify-between hover:bg-white/5 transition-colors"
                >
                  <span className="text-white font-semibold pr-4">{faq.question}</span>
                  <ChevronDown
                    className={`w-6 h-6 text-[#7CD4FD] flex-shrink-0 transition-transform duration-300 ${
                      expandedFAQ === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                <AnimatePresence>
                  {expandedFAQ === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 text-gray-400 leading-relaxed border-t border-white/10 pt-4">
                        {faq.answer}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>

          {/* Still have questions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 text-center p-8 rounded-3xl bg-gradient-to-br from-[#7CD4FD]/10 to-[#A78BFA]/10 border border-[#7CD4FD]/30"
          >
            <MessageCircle className="w-12 h-12 text-[#7CD4FD] mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-2">Noch Fragen?</h3>
            <p className="text-gray-400 mb-6">
              Unser Support-Team hilft Ihnen gerne weiter - 24/7 verfügbar
            </p>
            <Button
              onClick={() => navigate('/contact')}
              className="bg-gradient-to-r from-[#7CD4FD] to-[#1EA0E6] text-white font-semibold px-8 py-4 rounded-xl"
            >
              <Headphones className="w-5 h-5 mr-2" />
              Support kontaktieren
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="relative py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative p-12 rounded-3xl bg-gradient-to-br from-[#7CD4FD]/20 to-[#1EA0E6]/20 border-2 border-[#7CD4FD]/50 overflow-hidden"
          >
            {/* Glow effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#7CD4FD]/20 to-[#1EA0E6]/20 blur-3xl" />

            <div className="relative z-10 text-center">
              <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
                {t.cta?.title || "Bereit für intelligente Fahrzeugdiagnose?"}
              </h2>
              <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                {t.cta?.subtitle || "Starten Sie noch heute und sparen Sie Zeit und Geld"}
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
                <Button
                  onClick={() => setShowDemoModal(true)}
                  className="group relative overflow-hidden bg-gradient-to-r from-[#7CD4FD] to-[#1EA0E6] text-white font-bold px-12 py-8 rounded-2xl text-lg shadow-2xl shadow-blue-500/50 hover:shadow-blue-500/70 hover:scale-105 transition-all duration-300"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    {t.cta?.button || "Jetzt kostenlos starten"}
                    <Play className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Button>
              </div>

              <div className="flex items-center justify-center gap-6 text-sm text-gray-400">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#22C55E]" />
                  <span>{locale === 'de' ? 'Keine Kreditkarte erforderlich' : locale === 'en' ? 'No credit card required' : locale === 'it' ? 'Nessuna carta di credito richiesta' : 'Pas de carte de crédit requise'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#22C55E]" />
                  <span>{locale === 'de' ? 'Jederzeit kündbar' : locale === 'en' ? 'Cancel anytime' : locale === 'it' ? 'Cancella in qualsiasi momento' : 'Annulez à tout moment'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#22C55E]" />
                  <span>{locale === 'de' ? 'Sofort loslegen' : locale === 'en' ? 'Start instantly' : locale === 'it' ? 'Inizia subito' : 'Commencez immédiatement'}</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="relative py-20 px-6 border-t border-white/10">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 mb-6">
              <Sparkles className="w-4 h-4 text-[#7CD4FD]" />
              <span className="text-sm text-[#7CD4FD]">Bleiben Sie informiert</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                Newsletter abonnieren
              </span>
            </h2>
            
            <p className="text-lg text-gray-400 mb-8 max-w-2xl mx-auto">
              Erhalten Sie exklusive Automotive-Tipps, Produkt-Updates und Sonderangebote direkt in Ihr Postfach.
            </p>

            <div className="max-w-md mx-auto">
              <NewsletterSubscribeForm variant="full" />
            </div>

            <p className="text-xs text-gray-500 mt-4">
              Jederzeit abmeldbar. Keine Spam-Emails. 100% kostenlos.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative py-12 px-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Company */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img 
                  src="/branding/logo.svg" 
                  alt="Carcortex-ai Logo" 
                  style={{ height: '35px', width: 'auto' }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="items-center gap-2" style={{ display: 'none' }}>
                  <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#7CD4FD] to-[#1EA0E6] flex items-center justify-center">
                    <Wrench className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-xl font-bold text-white">Carcortex-ai</span>
                </div>
              </div>
              <p className="text-sm text-gray-400">
                Professionelle KI-Fahrzeugdiagnose für Werkstätten und Hobby-Schrauber
              </p>
            </div>

            {/* Product */}
            <div>
              <h4 className="text-white font-semibold mb-4">Produkt</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors cursor-pointer" onClick={(e) => { e.preventDefault(); document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }); }}>Preise</a></li>
                <li><a href="#vergleich" className="hover:text-white transition-colors">Vergleich</a></li>
                <li><a href="#faq" className="hover:text-white transition-colors">FAQ</a></li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="text-white font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/hilfe" className="hover:text-white transition-colors">Hilfe Center</a></li>
                <li><a href="/faq" className="hover:text-white transition-colors">FAQ</a></li>
                <li><a href="/kontakt" className="hover:text-white transition-colors">Kontakt</a></li>
                <li><a href="/status" className="hover:text-white transition-colors">Status</a></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-white font-semibold mb-4">Rechtliches</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/datenschutz" className="hover:text-white transition-colors">Datenschutz</a></li>
                <li><a href="/agb" className="hover:text-white transition-colors">AGB</a></li>
                <li><a href="/impressum" className="hover:text-white transition-colors">Impressum</a></li>
                <li><a href="/cookie-richtlinie" className="hover:text-white transition-colors">Cookie-Richtlinie</a></li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-col items-center md:items-start gap-3">
              <p className="text-sm text-gray-400">
                © 2025 Carcortex-ai. Alle Rechte vorbehalten.
              </p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-400">Teilen:</span>
                <SocialShareButtons 
                  url={window.location.origin}
                  title="CarCortex-AI - KI-gestützte Fahrzeugdiagnose"
                  description="Professionelle KI-Diagnosen für dein Fahrzeug. Bis zu 87% Kosten sparen!"
                />
              </div>
            </div>
            <div className="flex items-center gap-6">
              {trustBadges.map((badge, index) => (
                <div key={index} className="flex items-center gap-2 text-gray-500">
                  <badge.icon className="w-4 h-4" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </footer>

      {/* Demo Modal */}
      <DemoLoginModal 
        isOpen={showDemoModal} 
        onClose={() => setShowDemoModal(false)} 
      />
      
      {/* Feature Detail Modal */}
      <FeatureDetailModal 
        isOpen={selectedFeature !== null}
        onClose={() => setSelectedFeature(null)}
        feature={selectedFeature ? diagnoseFeatures[selectedFeature] : null}
      />

      {/* Aktionen Popup */}
      <AktionenPopup pageType="diagnose" />

      {/* API Documentation Modal - Coming Soon */}
      {showApiDocsModal && (
        <div className="fixed inset-0 bg-black/80 z-[200] flex items-center justify-center p-4" onClick={() => setShowApiDocsModal(false)}>
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#0F141A] rounded-2xl border border-cyan-500/30 p-8 max-w-md w-full text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-cyan-500/20 flex items-center justify-center">
              <Code className="w-8 h-8 text-cyan-400" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">API-Dokumentation</h3>
            <p className="text-gray-400 mb-6">
              Unsere API-Dokumentation befindet sich derzeit in Entwicklung und wird in Kürze verfügbar sein.
            </p>
            <div className="bg-cyan-500/10 rounded-lg p-4 mb-6">
              <p className="text-sm text-cyan-400">
                Interessiert an API-Zugang? Kontaktieren Sie uns unter <a href="mailto:api@carcortex-ai.com" className="underline">api@carcortex-ai.com</a>
              </p>
            </div>
            <Button onClick={() => setShowApiDocsModal(false)} className="bg-cyan-500 hover:bg-cyan-600 text-white">
              Verstanden
            </Button>
          </motion.div>
        </div>
      )}

      {/* System Status Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black/80 z-[200] flex items-center justify-center p-4" onClick={() => setShowStatusModal(false)}>
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#0F141A] rounded-2xl border border-green-500/30 p-8 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">System-Status</h3>
              <p className="text-gray-400">Alle Systeme laufen einwandfrei</p>
            </div>
            
            <div className="space-y-3 mb-6">
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <span className="text-gray-300">🌐 Web-App</span>
                <span className="text-green-400 font-medium">Online</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <span className="text-gray-300">🤖 KI-Diagnose</span>
                <span className="text-green-400 font-medium">Online</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <span className="text-gray-300">🔗 API</span>
                <span className="text-green-400 font-medium">Online</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <span className="text-gray-300">💳 Zahlungen</span>
                <span className="text-green-400 font-medium">Online</span>
              </div>
            </div>
            
            <p className="text-xs text-gray-500 text-center mb-4">
              Letztes Update: {new Date().toLocaleString('de-DE')}
            </p>
            
            <Button onClick={() => setShowStatusModal(false)} className="w-full bg-green-500 hover:bg-green-600 text-white">
              Schließen
            </Button>
          </motion.div>
        </div>
      )}
    </div>
  );
};

